import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheckCircle, faExclamationCircle, faGift, faUser } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';
import { DEFAULT_SHARE_RATIO } from '../../utils/helpers';

interface IndividualBalanceCardProps {
    participantId: string;
    participantName: string;
    balance: number;
    sponsorAmount?: number;
    // Shown beside the name when it is not 100%, so a smaller balance explains itself
    shareRatio?: number;
    onViewProfile: (participantId: string) => void;
}

const IndividualBalanceCard: React.FC<IndividualBalanceCardProps> = ({
                                                                         participantId,
                                                                         participantName,
                                                                         balance,
                                                                         sponsorAmount = 0,
                                                                         shareRatio = DEFAULT_SHARE_RATIO,
                                                                         onViewProfile
                                                                     }) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatCurrency();
    // Dòng tiền thực khi tất toán = số dư cá nhân trừ tiền tài trợ đang giữ (phải chuyển đi)
    const actualBalance = balance - sponsorAmount;
    const hasSponsorAmount = sponsorAmount > 0;
    // Phần người giữ tiền phải bỏ thêm từ tiền túi (ngoài tiền tài trợ đang cầm)
    const outOfPocket = Math.max(0, -balance);

    const isOwed = actualBalance > 0.01;
    const owes = actualBalance < -0.01;
    const isSettled = !isOwed && !owes;

    const config = {
        owed: {
            bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
            avatar: 'bg-green-100 dark:bg-green-900/30',
            text: 'text-green-600 dark:text-green-400',
            icon: faArrowRight,
            label: t('event.balances.status.shouldReceive')
        },
        owes: {
            bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
            avatar: 'bg-red-100 dark:bg-red-900/30',
            text: 'text-red-600 dark:text-red-400',
            icon: faExclamationCircle,
            label: t('event.balances.status.needsToPay')
        },
        settled: {
            bg: 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600',
            avatar: 'bg-gray-100 dark:bg-gray-800',
            text: 'text-gray-500 dark:text-gray-400',
            icon: faCheckCircle,
            label: t('event.balances.status.allSettled')
        }
    };

    const style = isOwed ? config.owed : owes ? config.owes : config.settled;

    return (
        <div className="glass-sub-panel rounded-lg hover:shadow-sm transition-all">
            {/* Main row */}
            <div
                className="grid grid-cols-[1fr_auto] sm:grid-cols-3 items-center gap-x-3 gap-y-1 px-3 py-2 cursor-pointer"
                onClick={() => onViewProfile(participantId)}
            >
                {/* Column 1: Name */}
                <div className="flex items-center min-w-0">
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center mr-2 flex-shrink-0 ${style.avatar}`}>
                        <FontAwesomeIcon icon={faUser} className={`text-xs ${style.text}`} />
                    </div>
                    <span className="font-medium text-md text-gray-900 dark:text-white truncate">
                        {participantName}
                    </span>
                    {shareRatio !== DEFAULT_SHARE_RATIO && (
                        <span
                            className="ml-2 flex-shrink-0 px-1.5 py-px rounded-full text-[10px] font-semibold tabular-nums bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                            title={t('event.balances.shareRatio', { ratio: shareRatio })}
                        >
                            {shareRatio}%
                        </span>
                    )}
                </div>

                {/* Column 2: Config (Icon + Label) — moves under the name on mobile */}
                <div className="flex items-center order-3 sm:order-none sm:justify-center col-span-2 sm:col-span-1">
                    <FontAwesomeIcon icon={style.icon} className={`text-xs ${style.text} mr-1`} />
                    <span className={`text-xs ${style.text}`}>
                        {style.label}
                    </span>
                </div>

                {/* Column 3: Amount */}
                <div className="flex items-center justify-end order-2 sm:order-none">
                    <div className={`font-semibold text-md ${style.text}`}>
                        {isSettled ? '0 ₫' : formatCurrency(Math.abs(actualBalance))}
                    </div>
                </div>
            </div>

            {/* Sponsor holder breakdown */}
            {hasSponsorAmount && (
                <div className="mx-3 pb-2 pt-1 border-t border-gray-200 dark:border-gray-600 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-blue-600 dark:text-blue-400">
                        <FontAwesomeIcon icon={faGift} className="mr-1" />
                        {t('event.balances.holder.holding')}:{' '}
                        <span className="font-semibold">{formatCurrency(sponsorAmount)}</span>
                    </span>
                    {owes && (
                        <>
                            <span className="text-gray-600 dark:text-gray-300">
                                {t('event.balances.holder.paidFromSponsor')}:{' '}
                                <span className="font-semibold">{formatCurrency(Math.min(sponsorAmount, -actualBalance))}</span>
                            </span>
                            <span className={outOfPocket > 0.01
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-600 dark:text-gray-300'
                            }>
                                {t('event.balances.holder.outOfPocket')}:{' '}
                                <span className="font-semibold">{formatCurrency(outOfPocket)}</span>
                            </span>
                        </>
                    )}
                    {isOwed && (
                        <span className="text-gray-600 dark:text-gray-300">
                            {t('event.balances.holder.fromMembers')}:{' '}
                            <span className="font-semibold">{formatCurrency(actualBalance)}</span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default IndividualBalanceCard;